/* global ProfileEnabled */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router';
import Dotdotdot from 'react-dotdotdot';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Loader from 'components/Loader';
import { renderImage, WATCH_RELATED_THUMBNAIL_IMAGE } from 'helpers/ImageHelper';
import { exposureEvent, EXPERIMENTS } from 'helpers/AmplitudeHelper';
import { handleVodSectionClicked } from 'components/VideoPlayerGlobal/Actions';
import {
  checkRecommendationIdOnClick,
  Events,
  getSegmentModelMetadata,
  handleAnalytics,
  PageNames,
  SegmentActionLocations,
  trackPageReferrerAnalyticsHook,
} from '@foxcorp/delta-web-analytics';

const VideoPlaylist = ({
  account,
  breakpoint,
  episodes,
  initialEpisodes,
  panels,
  panelVideo,
  recommendationIdData,
  setRecommendationId,
  theme,
  videoContainerHeight,
}) => {
  const [trackedAnalyticsReferrer, setTrackedAnalyticsReferrer] = useState(false);
  const [currentEpisodes, setCurrentEpisodes] = useState([]);
  const episodeContainerRef = useRef(null);
  const seasonNumber = panelVideo?.seasonNumber;
  const seasonName = panelVideo?.seasonName;
  const seriesName = panelVideo?.seriesName;
  const seasonText = seasonNumber ? `Season ${seasonNumber}` : '';
  const sectionTitle = `${seriesName} - ${seasonName || seasonText}`;
  const isLoading = episodes.length === 0;

  const generateEpisodeOrder = useCallback(
    playingItem => {
      let playingItemIndex;
      const newEpisodeOrder = initialEpisodes;
      newEpisodeOrder.forEach((episode, index) => {
        const isPlaying = playingItem?.guid === episode?.guid;
        if (isPlaying) playingItemIndex = index;
      });
      newEpisodeOrder.splice(playingItemIndex, 1);
      newEpisodeOrder.unshift(playingItem);
      setCurrentEpisodes(newEpisodeOrder);
    },
    [initialEpisodes],
  );

  useEffect(() => {
    exposureEvent({ experimentKey: EXPERIMENTS.WEB_PLAYER_PLAYLIST });
    if (episodes.length) {
      setCurrentEpisodes(episodes);
      let playingItemIndex;
      episodes.forEach((episode, index) => {
        const isPlaying = panelVideo?.guid === episode?.guid;
        if (isPlaying) playingItemIndex = index;
      });
      const playingEpisodeItem = episodes[playingItemIndex];
      if (playingEpisodeItem) {
        generateEpisodeOrder(playingEpisodeItem);
      }
    }
  }, [episodes, generateEpisodeOrder, panelVideo]);

  if (isLoading) return <Loader className={theme.playlistLoader} />;

  const handleClick = ({ name, description, itemIndex, originalItem, newRecIdData }) => {
    const id = originalItem?.id;
    const uID = originalItem?.uID ?? '';
    const showCode = originalItem?.showCode ?? '';
    const pageCollectionCount = panels?.member?.length - 1; // don't count the player panel
    const isAnonymouslyResumingPlayback = !account && originalItem?.percentWatched;
    trackPageReferrerAnalyticsHook({
      name: PageNames.VOD_SECTION,
      setTrackedAnalyticsReferrer,
      trackedAnalyticsReferrer,
    });
    checkRecommendationIdOnClick(recommendationIdData, newRecIdData, setRecommendationId);
    const vodClickedState = {
      anonymousResumeProfileUpsell: isAnonymouslyResumingPlayback && ProfileEnabled,
      anonymousResume: isAnonymouslyResumingPlayback && !ProfileEnabled,
      restartFromBeginning: false,
      isAuthError: false,
    };
    handleVodSectionClicked({ originalItem, vodClickedState });
    const modelMetaData = originalItem?.trackingData?.properties?.modelMetaData;
    if (id) {
      handleAnalytics({
        event: Events.PLAYLIST_BUTTON_ACTION,
        pauload: {
          page_action_type: 'Watch Now',
          page_collection_count: pageCollectionCount,
          page_collection_title: sectionTitle,
          page_item_title: name,
          page_item_subtitle: description,
          page_item_horizontal_position: itemIndex + 1,
          page_item_vertical_position: 1,
          page_action_location: SegmentActionLocations.PLAYER,
          page_recs_collection_model_id: modelMetaData?.modelId ?? ' ',
          page_recs_collection_request_id: modelMetaData?.requestId ?? ' ',
          page_recs_collection_result_set_id: modelMetaData?.resultSetId ?? ' ',
          page_recs_collection_source_id: modelMetaData?.sourceId ?? ' ',
          page_video_uid: uID || showCode,
          ...getSegmentModelMetadata(),
        },
      });
    }
    episodeContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    !!panelVideo && (
      <div
        ref={episodeContainerRef}
        className={theme.episodesContainer}
        style={{ height: videoContainerHeight }}
      >
        <h2>{sectionTitle}</h2>
        <ul>
          {!!currentEpisodes.length &&
            currentEpisodes.map((episode, itemIndex) => {
              const { name, images, description } = episode;
              const isPlaying = panelVideo?.guid === episode?.guid;
              return (
                <li>
                  <Link
                    className={classNames([theme.episodeLink, isPlaying && theme.disableEvent])}
                    to={`/watch/${episode?.id}`}
                    onClick={() =>
                      handleClick({ name, description, itemIndex, originalItem: episode })
                    }
                  >
                    <div className={theme.imgContainer}>
                      {renderImage({
                        alt: images?.videoList?.altText,
                        breakpoint,
                        imageType: WATCH_RELATED_THUMBNAIL_IMAGE,
                        src: images?.videoList?.raw,
                      })}
                      {isPlaying && <span>NOW PLAYING</span>}
                    </div>
                    <div>
                      <h4>
                        <Dotdotdot clamp={2}>{name}</Dotdotdot>
                      </h4>
                      <p>
                        <Dotdotdot clamp={3}>{description}</Dotdotdot>
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
        </ul>
      </div>
    )
  );
};

VideoPlaylist.propTypes = {
  breakpoint: PropTypes.string,
  episodes: PropTypes.arrayOf(PropTypes.shape({})),
  initialEpisodes: PropTypes.arrayOf(PropTypes.shape({})),
  originalItem: PropTypes.shape({}),
  panelVideo: PropTypes.shape({}),
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  setRecommendationId: PropTypes.func,
  theme: PropTypes.shape({}),
  videoContainerHeight: PropTypes.number,
};

export default VideoPlaylist;
